import { Navigate, Outlet, useLocation } from 'react-router';

import { useAuth } from './useAuth';
import { isInsurerUser } from './roles';

/**
 * De enige route(s) die de verzekeraar-rol mag bereiken.
 *
 * ⚠⚠ **Een nieuw verzekeraar-scherm dat hier niet bij komt is ONBEREIKBAAR, en dat
 * faalt STIL.** De redirect hieronder geeft geen melding: zij klikt, landt op "Te
 * beoordelen" en concludeert dat de link stuk is. Van buiten is dat niet te
 * onderscheiden van een scherm dat nog niet gebouwd is — terwijl het er gewoon staat
 * en het endpoint netjes antwoordt.
 */
const INSURER_ALLOWED_PREFIXES = [
    '/te-beoordelen', // de worklist (landing)
    '/dossiers/', // dossier-detail (read-only inzage binnen scope; BE gate't)
    '/mijn-gegevens', // gebruikersmenu (eigen gegevens / uitloggen)
    '/support', // Mijn supportmeldingen (story be-fe-support-melden-en-volgen, AC 37); de server scope't
    // ⚠⚠ TERUG OP 09-09-2026. De eigenaar: *"ik wil dat de verzekeraar ook een claimfilter
    //    applicatie krijgt. Hierin alleen de oordeelregels voor die maatschappij tonen.
    //    Bewerken en toevoegen moet ook mogelijk zijn."*
    //
    //    Deze regel en de top-level route in `claimFilterAudience.ts` zijn ÉÉN ingreep —
    //    de aantekening hieronder schreef allebei voor. Alleen de route verhuizen laat
    //    deze guard haar nog steeds STIL wegsturen; alleen deze regel zetten wijst naar
    //    een pad dat niet bestaat. Er staat een test op beide helften.
    '/oordeelsregels', // de oordeelsregels van háár eigen maatschappijen (BE scope't)
    //
    // ⚠ De geschiedenis blijft staan, want zij verklaart waarom het pad top-level is en
    //   niet onder `/claimfilter/*` zoals de rest van die app:
    //
    //    Op 04-09-2026 is deze regel juist WEGGEHAALD — met opzet.
    //
    //    Beslissing eigenaar toen: "voor system en support toevoegen aan de claimfilter
    //    applicatie. Hou de verzekeraar nog even buiten beschouwing". Het scherm stond
    //    daarom onder `/claimfilter/oordeelsregels` en erfde de system/support-poort van
    //    die app.
    //
    //    ⚠ HET BACKEND KENDE DE VERZEKERAAR TOEN AL: `/api/claimfilter-rules/*` geeft haar
    //      haar eigen maatschappijen. Wat ontbrak was uitsluitend haar weg ernaartoe — en
    //      dat is precies wat op 09-09 is gebouwd.
];

/**
 * Story `fe-be-verzekeraar-rol-scope-worklist` (2026-06-29) — route-containment
 * voor de verzekeraar-rol (open punt 6). Een ingelogde verzekeraar die handmatig
 * een interne app-route intypt (bv. `/dossiers`, `/planbord`, `/inkoop/...`)
 * wordt naar "Te beoordelen" geredirect — zo is geen intern scherm bereikbaar in
 * de kale ervaring. Niet-verzekeraars passeren ongemoeid (geen regressie op de
 * interne ervaring).
 *
 * Dit is de FE-laag van de afscherming; de echte data-scope (welke dossiers de
 * verzekeraar mag zien) zit BE-side (access-predicate + detail-GET-gate).
 */
export function InsurerRouteGuard() {
    const { user } = useAuth();
    const location = useLocation();

    if (isInsurerUser(user) && !mayInsurerReach(location.pathname)) {
        return <Navigate to="/te-beoordelen" replace />;
    }

    return <Outlet />;
}

/**
 * Mag de verzekeraar-rol dit pad bereiken?
 *
 * ⚠ Losgetrokken uit de component op 09-09-2026 zodat een test hem kan bevragen zonder
 * een router op te tuigen. Dat is niet alleen gemak: een nieuw verzekeraar-scherm dat
 * hier niet bij komt faalt STIL — zij landt op "Te beoordelen" en concludeert dat de
 * link stuk is. Zo'n regel hoort met een assertie vastgelegd te kunnen worden, naast de
 * route die hem nodig heeft.
 */
export function mayInsurerReach(path: string): boolean {
    return INSURER_ALLOWED_PREFIXES.some((p) =>
        p.endsWith('/') ? path.startsWith(p) : path === p || path.startsWith(p + '/'),
    );
}
